import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  Edit,
  Save,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  Settings,
  Lock,
  Clock,
  MapPin,
  Building
} from 'lucide-react';

const UserProfile = ({ isOpen, onClose }) => {
  const { user: currentUser, updateUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    avatar: null
  });
  const [errors, setErrors] = useState({});
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Image resizing helper function
  const resizeImage = (file, maxWidth = 512, maxHeight = 512, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          resolve(blob);
        }, file.type, quality);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Fetch user details when modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      fetchUserDetails();
    }
  }, [isOpen]);

  // Cleanup avatar preview when modal closes
  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      
      // Use currentUser data for immediate display
      if (currentUser) {
        setUser(currentUser);
        setFormData({
          name: currentUser.name || '',
          email: currentUser.email || '',
          username: currentUser.username || '',
          phone: currentUser.phone || '',
          current_password: '',
          new_password: '',
          confirm_password: '',
          avatar: null
        });
      }
      
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load profile data', {
        duration: 3000,
        icon: '❌',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = async (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'avatar' && files && files[0]) {
      const file = files[0];
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, avatar: 'Please select a valid image file (JPEG, PNG, JPG, GIF, WebP)' }));
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, avatar: 'File size must be less than 2MB' }));
        return;
      }
      
      // Clear previous errors
      setErrors(prev => ({ ...prev, avatar: null }));
      
      // Create preview
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
      
      // Resize image
      const resizedBlob = await resizeImage(file);
      setFormData(prev => ({ ...prev, avatar: resizedBlob }));
      
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
      // Clear errors when user starts typing
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: null }));
      }
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setLoading(true);
      setErrors({});

      // Prepare request body and headers
      let requestBody;
      let headers = {};

      if (formData.avatar) {
        // Use FormData for file uploads
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('username', formData.username);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('avatar', formData.avatar);

        // Add password data only if changing password
        if (formData.new_password) {
          formDataToSend.append('current_password', formData.current_password);
          formDataToSend.append('new_password', formData.new_password);
        }
        
        requestBody = formDataToSend;
        // Don't set Content-Type header for FormData, let the browser set it with boundary
      } else {
        // Use JSON for regular updates without file upload
        requestBody = JSON.stringify({
          name: formData.name,
          email: formData.email,
          username: formData.username,
          phone: formData.phone,
          // Add password data only if changing password
          ...(formData.new_password && {
            current_password: formData.current_password,
            new_password: formData.new_password
          })
        });
        headers = {
          'Content-Type': 'application/json'
        };
      }

      const response = await apiService.request(`/users/${currentUser.id}/profile`, {
        method: 'POST',
        body: requestBody,
        headers: headers
      });

      if (response.success) {
        toast.success('Profile updated successfully!', {
          duration: 3000,
          icon: '✅',
          style: {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: '#fff',
          },
        });
        
        // Update the user context
        updateUser(response.data);
        setUser(response.data);
        
        // Update form data with the latest user data and clear sensitive fields
        setFormData(prev => ({
          name: response.data.name || '',
          email: response.data.email || '',
          username: response.data.username || '',
          phone: response.data.phone || '',
          current_password: '',
          new_password: '',
          confirm_password: '',
          avatar: null
        }));
        
        // Clear avatar preview after successful upload
        if (avatarPreview) {
          URL.revokeObjectURL(avatarPreview);
          setAvatarPreview(null);
        }
        
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.message || 'Failed to update profile', {
          duration: 3000,
          icon: '❌',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setErrors({});
    
    // Clear avatar preview
    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
      setAvatarPreview(null);
    }
    
    // Reset form data to original values
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        username: user.username || '',
        phone: user.phone || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
        avatar: null
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">My Profile</h2>
                <p className="text-white/80 text-sm">Manage your account information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading profile...</span>
            </div>
          ) : user ? (
            <>
              {/* Profile Header */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200 p-6">
                <div className="flex items-center space-x-6">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {avatarPreview ? (
                        <img
                          src={avatarPreview}
                          alt="Avatar Preview"
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : user?.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name || 'User'}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        (user?.name || user?.username || 'U').charAt(0).toUpperCase()
                      )}
                    </div>
                    {editing && (
                      <label className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                        <Edit className="w-4 h-4 text-white" />
                        <input
                          type="file"
                          name="avatar"
                          accept="image/*"
                          onChange={handleInputChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {user.name || 'Unknown User'}
                    </h3>
                    <p className="text-gray-600 mb-1">@{user.username}</p>
                    <p className="text-gray-500 text-sm mb-3">{user.email}</p>
                    
                    {/* Status Badges */}
                    <div className="flex items-center space-x-2 flex-wrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        user.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status === 'active' ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Active</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            <span>Inactive</span>
                          </>
                        )}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        user.email_verified_at 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.email_verified_at ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Verified</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3 h-3" />
                            <span>Unverified</span>
                          </>
                        )}
                      </span>
                      {user.roles && user.roles.length > 0 && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 flex items-center space-x-1">
                          <Shield className="w-3 h-3" />
                          <span>{user.roles.length} Role{user.roles.length > 1 ? 's' : ''}</span>
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2">
                    {!editing ? (
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setEditing(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit Profile</span>
                        </button>
                        <button
                          type="button"
                          onClick={onClose}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={loading}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 flex items-center space-x-2"
                        >
                          <Save className="w-4 h-4" />
                          <span>{loading ? 'Saving...' : 'Save'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleCancel}
                          disabled={loading}
                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <User className="w-5 h-5 text-blue-600" />
                  <span>Personal Information</span>
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    {editing ? (
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your full name"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">
                        {user.name || 'Not provided'}
                      </div>
                    )}
                    {errors.name && (
                      <div className="text-red-500 text-sm mt-2">
                        {errors.name}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    {editing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your email"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">
                        {user.email || 'Not provided'}
                      </div>
                    )}
                    {errors.email && (
                      <div className="text-red-500 text-sm mt-2">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    {editing ? (
                      <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your username"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">
                        @{user.username || 'Not provided'}
                      </div>
                    )}
                    {errors.username && (
                      <div className="text-red-500 text-sm mt-2">
                        {errors.username}
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    {editing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your phone number"
                      />
                    ) : (
                      <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900">
                        {user.phone || 'Not provided'}
                      </div>
                    )}
                    {errors.phone && (
                      <div className="text-red-500 text-sm mt-2">
                        {errors.phone}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Security Settings */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Lock className="w-5 h-5 text-green-600" />
                  <span>Security Settings</span>
                </h4>
                
                <div className="space-y-6">
                  {/* Change Password Section */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Change Password</h5>
                    
                    <div className="space-y-4">
                      {/* Current Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Current Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="current_password"
                            value={formData.current_password}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                            placeholder="Enter current password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {errors.current_password && (
                          <div className="text-red-500 text-sm mt-2">
                            {errors.current_password}
                          </div>
                        )}
                      </div>

                      {/* New Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="new_password"
                            value={formData.new_password}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                            placeholder="Enter new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {errors.new_password && (
                          <div className="text-red-500 text-sm mt-2">
                            {errors.new_password}
                          </div>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="confirm_password"
                            value={formData.confirm_password}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                            placeholder="Confirm new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        {errors.confirm_password && (
                          <div className="text-red-500 text-sm mt-2">
                            {errors.confirm_password}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Role Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  <span>Role & Permissions</span>
                </h4>
                
                <div className="space-y-4">
                  {/* User Roles */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Roles
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                          >
                            <Shield className="w-4 h-4 mr-1" />
                            {role.name}
                          </span>
                        ))
                      ) : (
                        <span className="px-4 py-3 bg-gray-50 rounded-lg text-gray-500 text-sm">
                          No roles assigned
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Role Descriptions */}
                  {user.roles && user.roles.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Role Details
                      </label>
                      <div className="space-y-2">
                        {user.roles.map((role, index) => (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-gray-900">{role.name}</span>
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                {role.slug}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{role.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  <span>Account Information</span>
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* User ID */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User ID
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      <span className="text-sm font-mono bg-gray-200 px-2 py-1 rounded">
                        {user.id}
                      </span>
                    </div>
                  </div>

                  {/* Initials */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Initials
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {user.initials || (user.name || user.username || 'U').charAt(0).toUpperCase()}
                      </div>
                      <span className="font-medium">{user.initials || (user.name || user.username || 'U').charAt(0).toUpperCase()}</span>
                    </div>
                  </div>

                  {/* Member Since */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Member Since
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span>
                        {formatDate(user.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Last Updated */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Updated
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span>
                        {formatDate(user.updated_at)}
                      </span>
                    </div>
                  </div>

                  {/* Email Verification */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Verification
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      {user.email_verified_at ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-green-700">Verified</span>
                          <span className="text-xs text-gray-500 ml-auto">
                            {formatDate(user.email_verified_at)}
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-yellow-500" />
                          <span className="text-yellow-700">Not Verified</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Account Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Status
                    </label>
                    <div className="px-4 py-3 bg-gray-50 rounded-lg text-gray-900 flex items-center space-x-2">
                      {user.status === 'active' ? (
                        <>
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-green-700">Active</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-4 h-4 text-red-500" />
                          <span className="text-red-700">Inactive</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No user data available</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
